import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import useGlobale from './global';

const Sidebar = ({ isOpen, menuItems, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isNavigating, setIsNavigating] = useState(false);
    const [logoError, setLogoError] = useState(false);

    // Close sidebar automatically on mobile when screen resizes
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 1024 && isOpen) {
                onClose();
            }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isOpen, onClose]);

    // Fonction de navigation améliorée
    const handleNavigation = (path) => {
        if (location.pathname === path) return;
        
        setIsNavigating(true);
        
        // Petit délai pour l'animation
        setTimeout(() => {
            navigate(path);
            setIsNavigating(false);
            
            // Fermer la sidebar sur mobile
            if (window.innerWidth < 1024) {
                onClose();
            }
        }, 150);
    };
    
    return (
        <>
            {/* Overlay for mobile avec animation */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm lg:hidden z-40 transition-all duration-300"
                    onClick={onClose}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar avec design professionnel */}
            <aside
                className={`
                    fixed lg:sticky inset-y-0 lg:top-0 left-0 
                    w-[280px] lg:w-64
                    bg-gradient-to-b from-white via-orange-50/30 to-green-50/20
                    shadow-xl
                    transform transition-all duration-300 ease-in-out
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                    lg:translate-x-0 
                    z-50
                    flex flex-col
                    h-screen
                    border-r-2 border-orange-200
                `}
            >
                {/* Mobile close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 lg:hidden p-2 rounded-lg hover:bg-orange-100 text-orange-600 transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Logo Section - Design professionnel avec couleurs */}
                <div className="flex items-center justify-center py-6 px-4 bg-gradient-to-r from-orange-500 to-orange-600 shadow-md">
                    <div className="flex items-center justify-center w-full">
                        {!logoError ? (
                            <div className="bg-white px-4 py-2 rounded-lg shadow-lg">
                                <img 
                                    src="/fonap-lo.png" 
                                    alt="Logo FONAP" 
                                    className="h-10 w-auto object-contain"
                                    onError={() => setLogoError(true)}
                                />
                            </div>
                        ) : (
                            <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg shadow-lg">
                                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white font-bold text-lg">F</span>
                                </div>
                                <span className="text-lg font-bold text-gray-800">FONAP</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation - Design minimaliste et professionnel avec couleurs */}
                <nav className="flex-1 px-3 py-4 overflow-y-auto">
                    <div className="space-y-1">
                        {menuItems && menuItems.length > 0 ? menuItems.map((item, index) => {
                            // Gérer les dividers
                            if (item.isDivider) {
                                return (
                                    <div key={index} className="pt-4 pb-2">
                                        <p className="px-3 text-xs font-semibold text-green-700 uppercase tracking-wider">
                                            {item.label}
                                        </p>
                                    </div>
                                );
                            }

                            const isActive = location.pathname === item.path;
                            return (
                                <button
                                    key={index}
                                    onClick={() => handleNavigation(item.path)}
                                    disabled={isNavigating}
                                    className={`
                                        flex items-center w-full
                                        px-3 py-2.5
                                        text-sm font-medium rounded-lg
                                        transition-all duration-200
                                        ${isActive
                                            ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md'
                                            : 'text-gray-700 hover:bg-green-50 hover:text-green-700'
                                        }
                                        ${isNavigating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                    `}
                                >
                                    <div className={`
                                        flex items-center justify-center w-5 h-5 mr-3
                                        ${isActive ? 'text-white' : 'text-orange-500'}
                                    `}>
                                        {React.cloneElement(item.icon, {
                                            className: 'w-5 h-5'
                                        })}
                                    </div>
                                    <span className="flex-1 text-left font-medium">
                                        {item.label}
                                    </span>
                                </button>
                            );
                        }) : (
                            <div className="p-4 text-center text-gray-400 text-sm">
                                <p>Chargement du menu...</p>
                            </div>
                        )}
                    </div>
                </nav>


            </aside>
        </>
    );
};

export default Sidebar;